'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import AOS from 'aos';
import 'aos/dist/aos.css';

export default function Companies() {
  const { isRTL, currentLanguage } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCompanies, setVisibleCompanies] = useState(new Set());
  const [companiesTitle, setCompaniesTitle] = useState('');
  const companiesRef = useRef([]);
  
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const res = await fetch('/api/footer');
        if (!res.ok) {
          throw new Error('Failed to fetch footer data');
        }
        const data = await res.json();
        setCompaniesTitle(data.footerText.companySection?.title?.[currentLanguage] || '');
      } catch (err) {
        console.error('Error fetching footer data:', err);
        setError('Failed to load section title');
      }
    };

    fetchFooterData();
  }, [currentLanguage]);

  useEffect(() => {
    // Fetch companies data when component mounts
    async function fetchCompanies() {
      try {
        const response = await fetch('/api/companies');
        
        if (!response.ok) {
          throw new Error('Failed to fetch companies');
        }
        
        const data = await response.json();
        setCompanies(data.companies);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError('Failed to load companies data');
        setLoading(false);
      }
    }
    
    fetchCompanies();
  }, []);
  
  // Set up intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCompanies(prev => new Set([...prev, entry.target.dataset.index]));
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the element is visible
        rootMargin: '0px'
      }
    );

    // Observe all company cards
    companiesRef.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [companies.length]);

  console.log('companies:', companies);
  
  // Show loading state
  if (loading) {
    return (
      <section className="bg-white w-full">
        <div className="w-full bg-gradient-to-b from-blue-50 to-blue-200 px-6 py-16">
          <h2 className="text-2xl font-bold text-center text-black mb-12">
            {companiesTitle}
          </h2>
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </section>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <section className="bg-white w-full">
        <div className="w-full bg-gradient-to-b from-blue-50 to-blue-200 px-6 py-16">
          <h2 className="text-2xl font-bold text-center text-black mb-12">
            {companiesTitle}
          </h2>
          <div className="text-center text-red-500 py-10">
            {error}
          </div>
        </div>
      </section>
    );
  }
  
  
  return (
    // Companies Section
    <section id="companies-section" className="bg-white w-full">
      <div className="w-full bg-gradient-to-b from-blue-50 to-blue-200 px-[4%] sm:px-[5%] py-[5vh] pt-[8vh]">
        
        <h2 
          className={`text-[2rem] sm:text-[2.5rem] md:text-[3rem] font-black text-left text-black mb-[3vh] ${isRTL ? 'text-right' : ''}`}
          data-aos="fade-up"
        >
          {companiesTitle}
        </h2>
        <div className="mt-[2vh] grid grid-cols-1 md:grid-cols-2 gap-[2rem] w-full">
          {companies.map((company, index) => (
            <div 
              key={company.id} 
              className="group relative bg-white p-[1.5rem] rounded-lg shadow w-full flex flex-col md:flex-row gap-[1.5rem] overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg"
              data-aos="fade-up"
              data-aos-delay={index * 100} // Stagger the animations
            >
              {/* Wave animation background */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#295CAD] to-[#78C9FF] translate-y-full transition-transform duration-1000 ease-in-out group-hover:translate-y-0 
                before:absolute before:inset-0 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAwIDEyMCI+PHBhdGggZD0iTTAsMTIwIEMxMDAsMTIwIDIwMCw5MCA0MDAsNjAgQzYwMCwzMCA4MDAsNjAgMTAwMCw5MCBMMTI0MCwxMjAgTDEyMDAsMTQwIEwwLDE0MCBaIiBmaWxsPSJ3aGl0ZSIgZmlsbC1vcGFjaXR5PSIwLjIiLz48L3N2Zz4=')] 
                before:bg-repeat-x before:animate-wave before:opacity-50"></div>

              {/* Second wave layer for more fluid look */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#295CAD] to-[#78C9FF] translate-y-full transition-transform duration-1000 delay-75 ease-in-out group-hover:translate-y-0 
                before:absolute before:inset-0 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAwIDEyMCI+PHBhdGggZD0iTTAsMTIwIEMyMDAsMTIwIDMwMCw4MCA2MDAsNDAgQzkwMCw4MCAxMDAwLDEyMCAxMjAwLDEyMCBMMTIwMCwxNDAgTDAsMTQwIFoiIGZpbGw9IndoaXRlIiBmaWxsLW9wYWNpdHk9IjAuMyIvPjwvc3ZnPg==')] 
                before:bg-repeat-x before:animate-wave-slow before:opacity-30"></div>

              {/* Third wave layer for enhanced parabolic effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#295CAD] to-[#78C9FF] translate-y-full transition-transform duration-1000 delay-150 ease-in-out group-hover:translate-y-0 
                before:absolute before:inset-0 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAwIDEyMCI+PHBhdGggZD0iTTAsMTIwIEMzMDAsMTIwIDQ1MCwyMCA2MDAsMjAgQzc1MCwyMCA5MDAsMTIwIDEyMDAsMTIwIEwxMjAwLDE0MCBMMC4xNDAIFoiIGZpbGw9IndoaXRlIiBmaWxsLW9wYWNpdHk9IjAuMiIvPjwvc3ZnPg==')] 
                before:bg-repeat-x before:animate-wave-slower before:opacity-20"></div>

              <div className={`relative z-10 w-full md:w-1/2 transition-transform duration-300 ease-in-out group-hover:scale-105 ${isRTL ? 'md:order-2' : 'md:order-1'}`}>
                <img 
                  src={company.imageUrl}
                  alt={company.imageAlt[currentLanguage]} 
                  className="rounded-lg w-full h-full object-cover"
                />
              </div>
              <div className={`relative z-10 w-full md:w-1/2 flex flex-col ${isRTL ? 'md:order-1 text-right' : 'md:order-2 text-left'}`}>
                <h3 className="text-[1.5rem] sm:text-[1.75rem] font-bold text-black group-hover:text-white transition-colors duration-500">
                  {company.title[currentLanguage]}
                </h3>
                <p className="mt-[2vh] md:mt-[4vh] text-[1rem] sm:text-[1.125rem] text-black group-hover:text-white transition-colors duration-500">
                  {company.description[currentLanguage]}
                </p>
                <a 
                  href={company.buttonUrl} 
                  className={`mt-[2vh] md:mt-[4vh] inline-block text-[1rem] sm:text-[1.125rem] font-semibold text-black group-hover:text-white transition-colors duration-500 hover:translate-x-2 ${isRTL ? 'flex items-center flex-row-reverse' : ''}`}
                >
                  {company.button[currentLanguage]} {isRTL ? '' : '>'}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

